import { useMemo } from "react";
import { merge } from "lodash";
import { parseJSON } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { TwrorResult } from "@api/v1/model";
import { addAreaValues, getInterval } from "@helpers";
import {
  calculatePercentsForPerformance,
  SecuritiesChartDataWithDate,
  SecuritiesChartSelectedType,
} from "@components";
import { useInvestmentHistoricQuotes } from "@features/investments/investment/hooks/useInvestmentHistoricQuotes";
import { mergeComparisonInvestmentByDate } from "@features/investments/investment/utils/mergeQuotes";
import { usePerformanceByAssetClass } from "./usePerformanceByAssetClass";

const convertToChartData = <T extends { date: Date }>(
  fieldName: string,
  data?: TwrorResult[]
): T[] => {
  if (!data) return [];
  return data.map(
    ({ date, portfolioValue }) =>
      ({
        date: parseJSON(date),
        [fieldName]: Math.round(portfolioValue * 100) / 100,
      }) as any
  );
};

export const convertToPercentChartData = <T extends { date: Date }>(
  fieldName: string,
  data?: TwrorResult[]
): T[] => {
  if (!data) return [];
  return data.map(
    ({ date, twror }) =>
      ({
        date: parseJSON(date),
        [fieldName]: twror,
      }) as any
  );
};

export interface InvestmentsChartDataStatistic {
  portfolioValue: number;
  performance: number;
  changeToday: number;
  investedCapital: number;
  changeTodayAbs: number;
  valuationGain: number;
  valuationGainPercent: number;
  irr: number;
}

export const useInvestmentsChartData = (
  accountIds: number[],
  startDate?: Date,
  endDate?: Date,
  chartType: "performance" | "valuation" = "valuation",
  comparedTicker?: string
) => {
  const isValuationChart = chartType === "valuation";
  const interval = getInterval(startDate, endDate);

  const query = usePerformanceByAssetClass({
    accountIds,
    startDate,
    endDate,
    interval,
  });
  const converterCallback = isValuationChart
    ? convertToChartData
    : convertToPercentChartData;

  const totalPerformance = converterCallback<{
    date: Date;
    totalValue: number;
  }>("totalValue", query.total?.twrorResults);

  const allHoldings = converterCallback<{
    date: Date;
    holdingsValue: number;
  }>("holdingsValue", query.allHoldings?.twrorResults);

  const fonds = converterCallback<{
    date: Date;
    fondsValue: number;
  }>("fondsValue", query.funds?.twrorResults);

  const etf = converterCallback<{
    date: Date;
    etfsValue: number;
  }>("etfsValue", query.etfs?.twrorResults);

  const derivative = converterCallback<{
    date: Date;
    derivativeValue: number;
  }>("derivativeValue", query.derivative?.twrorResults);

  const bonds = converterCallback<{
    date: Date;
    bondsValue: number;
  }>("bondsValue", query.bonds?.twrorResults);

  const mergedData: SecuritiesChartDataWithDate[] = merge(
    totalPerformance,
    allHoldings,
    fonds,
    etf,
    derivative,
    bonds
  );

  const {
    data: comparisonHistoryQuotes = [],
    isPending: historicQuotesIdle,
    isLoading: historicQuotesLoading,
  } = useInvestmentHistoricQuotes(
    {
      tickerSymbol: comparedTicker,
      interval,
      from: startDate?.toISOString(),
      to: endDate?.toISOString(),
    },
    {
      staleTime: Infinity,
      enabled: Boolean(comparedTicker),
      placeholderData: keepPreviousData,
    }
  );

  const chartData = comparedTicker
    ? mergeComparisonInvestmentByDate(mergedData, comparisonHistoryQuotes)
    : mergedData;

  const data = addAreaValues(chartData, "totalValue");

  const typesWithData: SecuritiesChartSelectedType[] = [];

  if (query.allHoldings?.hasData) {
    typesWithData.push("holdings");
  }

  if (query.funds?.hasData) {
    typesWithData.push("funds");
  }

  if (query.etfs?.hasData) {
    typesWithData.push("etfs");
  }
  if (query.derivative?.hasData) {
    typesWithData.push("derivative");
  }
  if (query.bonds?.hasData) {
    typesWithData.push("bonds");
  }

  const twrorResult =
    query.total?.twrorResults[query.total.twrorResults.length - 1];
  const cashFlowResult =
    query.total?.cashFlowResults[query.total.cashFlowResults.length - 1];
  const irrResult = query.total?.irrResults[query.total.irrResults.length - 1];

  const portfolioValue = twrorResult?.portfolioValue ?? 0;
  const performance = (twrorResult?.twror ?? 1) - 1;
  const changeToday = query.total?.changeToday?.performance ?? 0;
  const investedCapital = cashFlowResult?.investedCapital ?? 0;
  const changeTodayAbs =
    query.total?.changeToday?.changeAbsCashFlowAdjusted ?? 0;
  const valuationGain = portfolioValue - investedCapital;
  const valuationGainPercent = portfolioValue / investedCapital - 1;
  const irr = irrResult?.irr ?? 0;

  const statistic: InvestmentsChartDataStatistic = useMemo(
    () => ({
      portfolioValue,
      performance,
      changeToday,
      investedCapital,
      changeTodayAbs,
      valuationGain,
      valuationGainPercent,
      irr,
    }),
    [
      portfolioValue,
      performance,
      changeToday,
      investedCapital,
      changeTodayAbs,
      valuationGain,
      valuationGainPercent,
      irr,
    ]
  );

  return {
    data: isValuationChart
      ? data
      : calculatePercentsForPerformance(data, Boolean(comparedTicker)),
    typesWithData,
    statistic: statistic ?? {},
    isPending: query.isPending && historicQuotesIdle,
    isLoading: query.isLoading || historicQuotesLoading,
    isFetching: query.isFetching,
  };
};
